import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { IMachineForUserOwnTractor, IUserOwnTractor } from '../../../api/user/queries/getUsersOwnTractors';
import { RadioCard } from '../../../components/common/Radio/RadioCard';
import { ChipColorType } from '../../../components/common/chip/Chip';
import { MainHead } from '../../../components/common/head/MainHead';
import { AnimateLoading } from '../../../components/common/loading/AnimateLoading';
import { MachineApprovalStatus } from '../../../constants/machine/machine.constant';
import { useGetUsersOwnTractors } from '../../../hooks/api/user/queries/useGetUsersOwnTractors';
import { useLayout } from '../../../hooks/screen/useLayout';
import { useAppDispatch } from '../../../hooks/store/useStore';
import { userOwnTractorSurveySlice, userOwnTractorSurveyStateType } from '../../../store/userOwnTractorSurveySlice';
import { CreateOwnTractorButton } from './components/CreateOwnTractorButton';

export const MyInfoOwnTractorListPage = () => {
  const [selectedUserOwnTractor, setSelectedUserOwnTractor] = useState<IUserOwnTractor>();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const userOwnTractorActions = userOwnTractorSurveySlice.actions;

  const { data: usersOwnTractors, isLoading: isGetUsersOwnTractorsLoading } = useGetUsersOwnTractors({
    searchParam: { machineApprovalStatus: 'ALL' },
  });

  const getChipData = (machine: IMachineForUserOwnTractor): { color: ChipColorType; text: string } | null => {
    if (!machine || !machine.approvalStatus) {
      return {
        color: 'tertiary',
        text: '재등록 필요',
      };
    }
    switch (machine.approvalStatus) {
      case MachineApprovalStatus.PSWAITING:
      case MachineApprovalStatus.WAITING:
        return {
          color: 'tertiary',
          text: '정보 확인중',
        };
      case MachineApprovalStatus.REJECT:
        return {
          color: 'secondary',
          text: '등록 불가',
        };
      default:
        return null;
    }
  };

  useLayout({
    fixedHeader: <MainHead backButton={{ isShow: true }} headerTitle={'보유 트랙터 관리'} />,
    fixedHeaderHeight: 40,
    fixedFooter: (
      <>
        <CreateOwnTractorButton />,
        <ButtonSection>
          <BlurredSection />
          <ConfirmButton
            type={'button'}
            disabled={!selectedUserOwnTractor || !selectedUserOwnTractor.id}
            onClick={() => {
              if (!selectedUserOwnTractor || !selectedUserOwnTractor.id) {
                return;
              }

              const payload: Partial<userOwnTractorSurveyStateType> = {
                ownTractorIdForEdit: selectedUserOwnTractor?.id,
                marketPrice: selectedUserOwnTractor?.marketPrice
                  ? Number(selectedUserOwnTractor?.marketPrice) / 10000
                  : undefined,
              };

              if (selectedUserOwnTractor.machine?.approvalStatus === MachineApprovalStatus.APPROVAL) {
                Object.assign(payload, {
                  isApprovedTractor: true,
                  manufacturedYear: selectedUserOwnTractor?.manufacturedYear,
                  operationTime: selectedUserOwnTractor?.operationTime,
                  brand: selectedUserOwnTractor.brand.name,
                  brandId: selectedUserOwnTractor.brand.id,
                  model: selectedUserOwnTractor.machine.model,
                  engineOutputInPs: selectedUserOwnTractor.machine.engineOutputInPs,
                });
              }
              dispatch(userOwnTractorActions.changeOwnTractorSurveyForEdit(payload));
              navigate('/survey/progress/own-tractor/required-input-overview');
            }}
          >
            <ConfirmButtonText>{'수정'}</ConfirmButtonText>
          </ConfirmButton>
        </ButtonSection>
      </>
    ),
    fixedFooterHeight: 72,
  });

  if (isGetUsersOwnTractorsLoading) {
    return <AnimateLoading />;
  }

  if (usersOwnTractors) {
    return (
      <Container>
        <RadioCardContainer>
          {usersOwnTractors.map((tractor) => {
            const chipData = getChipData(tractor.machine);
            const isDisabled = tractor.machine && tractor.machine.approvalStatus !== MachineApprovalStatus.APPROVAL;

            return (
              <RadioCard
                key={tractor.id}
                title={tractor.brand?.name}
                label={tractor.machine?.model}
                onClick={() => {
                  setSelectedUserOwnTractor(tractor);
                }}
                isActive={selectedUserOwnTractor?.id === tractor.id}
                disabled={isDisabled}
                {...(chipData && { chip: chipData })}
              />
            );
          })}
        </RadioCardContainer>
      </Container>
    );
  }

  return <></>;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1 0 0;
  align-self: stretch;
`;

const RadioCardContainer = styled.div`
  display: flex;
  padding: var(--radius-large, 16px) var(--radius-large, 16px) 0px var(--radius-large, 16px);
  flex-direction: column;
  align-items: flex-start;
  gap: var(--radius-large, 16px);
  align-self: stretch;
`;

const ButtonSection = styled.div`
  position: relative;
  display: flex;
  width: 100%;

  padding: 0px 16px 32px 16px;
  background-color: white;

  justify-content: center;
  align-items: center;

  gap: 8px;
`;

const BlurredSection = styled.div`
  width: 100%;
  height: 40px;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: var(--radius-large, 16px);
  gap: var(--radius-small, 4px);

  border-radius: var(--radius-none, 0px);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 63.96%);

  position: absolute;
  top: -40px;
`;

const Button = styled.button`
  width: 100%;
  height: 40px;
  max-height: 40px;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: 16px;

  gap: 4px;

  border-radius: var(--radius-medium, 8px);

  &:disabled {
    opacity: 0.3;
  }

  cursor: pointer;
`;

const ButtonText = styled.span`
  /* System/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 11.9px */
`;

const ConfirmButton = styled(Button)`
  background: var(--Light-Scheme-Color-primary-blue, #445eff);
`;

const ConfirmButtonText = styled(ButtonText)`
  color: var(--Light-Scheme-Monochrome-white, #fff);
`;
